import { TFunction } from 'i18next'
import { z } from 'zod'

import { RecurringInvoiceDelivery } from '@features/recurringInvoices/enums/recurringInvoiceDelivery'
import { RecurringInvoiceDuration } from '@features/recurringInvoices/enums/recurringInvoiceDuration'
import { RecurringInvoiceFrequency } from '@features/recurringInvoices/enums/recurringInvoiceFrequency'

import { formatDateToString } from '../../../../../../utils/formatDateToString'
import { isValidEmail } from '../../../../../../utils/isValidEmail'

interface GetRecurringInvoiceSchemaOptions {
  t: TFunction
}

export const getRecurringInvoiceFormSchema = ({ t }: GetRecurringInvoiceSchemaOptions) => {
  return z
    .object({
      firstInvoiceDate: z
        .date({
          message: t('required_field'),
        })
        .superRefine((firstInvoiceDate, ctx) => {
          // normalize dates to ignore the time part when comparing
          const formattedFirstInvoiceDate = formatDateToString(firstInvoiceDate)
          const formattedToday = formatDateToString(new Date())

          if (formattedFirstInvoiceDate && formattedFirstInvoiceDate < formattedToday) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(
                'external_invoices.editor.form.recurring_invoice_settings.field.first_invoice_date.error.min.today',
              ),
            })
          }
        }),
      frequency: z.nativeEnum(RecurringInvoiceFrequency),
      duration: z.nativeEnum(RecurringInvoiceDuration),
      delivery: z.nativeEnum(RecurringInvoiceDelivery),
      deliveryNotification: z.boolean(),
      occurrences: z.number().min(1).max(9999),
      message: z.object({
        to: z.array(z.string()).refine((value) => value.every((email) => isValidEmail(email)), {
          message: t(
            'external_invoices.editor.form.recurring_invoice_settings.email_template.modal.field.to.error.valid_email',
          ),
        }),
        body: z.string(),
        acceptStripePayment: z.boolean().optional(),
      }),
    })
    .superRefine((values, ctx) => {
      if (values.delivery !== RecurringInvoiceDelivery.CreateAndSend) {
        return
      }

      if (!values.message?.to.length) {
        const message = t(
          'external_invoices.editor.form.recurring_invoice_settings.email_template.modal.field.to.error.required',
        )
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['message'] })
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['message', 'to'] })
      }

      if (!values.message?.body) {
        const message = t(
          'external_invoices.editor.form.recurring_invoice_settings.email_template.modal.field.email_message.error.required',
        )
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['message'] })
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['message', 'body'] })
      }
    })
}
